import type { ReferencedEntry } from 'contentful';
import React from 'react';

import type { HasBackgroundColor, HasColor } from 'api/contentful/contentTypes.cloverdotcom';
import { getField } from 'utils/EntryUtil';

import { applyEntryDataAttributes } from './EntryDataWrapper';
import type { IEntryWrapperProps } from './IEntryWrapperProps';
import { withEntryAttributes } from './WithEntryAttributes';

/**
 * Useful for standardizing the style attribute of components which render contentful entries
 * that have configurable style fields
 *
 * currently only supports: color and backgroundColor
 *
 * it is up to the wrapped component to properly apply the style attribute
 *
 * @param WrappedComponent the component to wrap
 */
export function applyEntryStyleAttributes<
  T extends HasBackgroundColor & HasColor,
  E extends ReferencedEntry<T> = ReferencedEntry<T>,
  P extends IEntryWrapperProps<T, E> = IEntryWrapperProps<T, E>,
>(WrappedComponent: React.ComponentType<P>) {
  return applyEntryDataAttributes<T, E, P>(
    withEntryAttributes<T, E, P>(
      ({ entry, style }) =>
        ({
          style: {
            ...style,
            backgroundColor: getField(entry, 'backgroundColor'),
            color: getField(entry, 'color'),
          },
        } as P),
    )(WrappedComponent),
  );
}
